import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/store/authStore'
import { useTenantStore } from '@/store/tenantStore'
import { useEffect, useRef } from 'react'
import { Toaster } from 'sonner'
import { canAccessAdmin, canAccessLearner, isDualRole } from '@/utils/roleUtils'
import { DevToolsProvider, isDevToolsVisible } from '@quebon/shared-learning'

// Layouts
import MainLayout from '@/components/layout/MainLayout'
import AdminLayout from '@/components/layout/AdminLayout'

// Brand Page (landing / invalid tenant)
import BrandPage from '@/pages/BrandPage'
import LandingPage from '@/pages/LandingPage'
import { initReferrer } from '@/utils/referrer'

// ref 파라미터를 라우터 렌더 전에 즉시 저장
initReferrer()

// Auth Pages
import LoginPage from '@/pages/auth/LoginPage'
// RegisterPage 제거 — B2B는 Admin이 계정 생성, 자체 가입 불가
import FindAccountPage from '@/pages/auth/FindAccountPage'
import ResetPasswordPage from '@/pages/auth/ResetPasswordPage'
import AccountSetupPage from '@/pages/auth/AccountSetupPage'

// Admin Pages
import AdminDashboard from '@/pages/admin/AdminDashboard'
import DepartmentManagement from '@/pages/admin/DepartmentManagement'
import SpecialGroupManagement from '@/pages/admin/SpecialGroupManagement'
import ClassManagement from '@/pages/admin/ClassManagement'
import MemberManagement from '@/pages/admin/MemberManagement'
import ProductManagement from '@/pages/admin/ProductManagement'
import ProductDetail from '@/pages/admin/ProductDetail'
import ProgressReport from '@/pages/admin/ProgressReport'
import NotesPage from '@/pages/admin/NotesPage'
import AdminMessagesPage from '@/pages/admin/messages/index'
import AdminMessageComposePage from '@/pages/admin/messages/compose'
import AdminMessageDetailPage from '@/pages/admin/messages/[id]'
import AdminSupport from '@/pages/admin/AdminSupport'
import ContractManagement from '@/pages/admin/ContractManagement'
import PrintSamplePage from '@/pages/admin/PrintSamplePage'

// Learner Pages
import MyCourses from '@/pages/learner/MyCourses'
import MyDashboard from '@/pages/learner/MyDashboard'
import MyAIQ from '@/pages/learner/MyAIQ'
import MySupport from '@/pages/learner/MySupport'
import MyProfile from '@/pages/learner/MyProfile'
import NewLearningPage from '@/pages/learner/NewLearningPage'
import QuizTestPage from '@/pages/learner/QuizTestPage'
import ToolTestPage from '@/pages/learner/ToolTestPage'
import QuetureTestPage from '@/pages/learner/QuetureTestPage'
import GameTestPage from '@/pages/game/GameTestPage'
import QuizReviewPage from '@/pages/learner/QuizReviewPage'
import LearningHistory from '@/pages/learner/LearningHistory'
import LearnerMessagesPage from '@/pages/messages/index'
import LearnerMessageDetailPage from '@/pages/messages/[id]'

// AIQ Pages
import PreAiqTest from '@/pages/learner/aiq/PreAiqTest'
import PostAiqTest from '@/pages/learner/aiq/PostAiqTest'
import AiqResult from '@/pages/learner/aiq/AiqResult'
import AiqReport from '@/pages/learner/aiq/AiqReport'
import AiqClassReport from '@/pages/admin/AiqClassReport'
import AiqLearnerReport from '@/pages/admin/AiqLearnerReport'

// Default Redirect Component
function DefaultRedirect() {
  const { user, currentMode } = useAuthStore()

  // Dual role인 경우 currentMode에 따라 리다이렉트
  if (isDualRole(user?.role)) {
    return <Navigate to={currentMode === 'learner' ? '/my/courses' : '/admin'} replace />
  }

  // Admin 권한이 있으면 admin 대시보드로
  if (canAccessAdmin(user?.role)) {
    return <Navigate to="/admin" replace />
  }

  // 그 외는 learner 내 과정으로
  return <Navigate to="/my/courses" replace />
}

// Protected Route Component
function ProtectedRoute({
  children,
  adminOnly = false,
  learnerOnly = false
}: {
  children: React.ReactNode,
  adminOnly?: boolean,
  learnerOnly?: boolean
}) {
  const { isAuthenticated, user, isLoading, hasCheckedAuth, requiresSetup } = useAuthStore()

  // 인증 확인이 완료되지 않았거나 확인 중이면 로딩 표시
  if (!hasCheckedAuth || isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="inline-block w-8 h-8 rounded-full border-b-2 animate-spin border-primary-600"></div>
          <p className="mt-4 text-gray-600">로딩 중...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  // 초기 설정 필요 시 설정 페이지로 리다이렉트
  if (requiresSetup) {
    return <Navigate to="/auth/setup" replace />
  }

  // Admin-only route: admin, dual_role 허용
  if (adminOnly && !canAccessAdmin(user?.role)) {
    return <Navigate to="/my/courses" replace />
  }

  // Learner-only route: learner, dual_role 허용
  if (learnerOnly && !canAccessLearner(user?.role)) {
    return <Navigate to="/admin" replace />
  }

  return <>{children}</>
}

// 쿼리 파라미터를 보존하며 / 로 리다이렉트
function RedirectToHome() {
  const { search } = useLocation()
  return <Navigate to={`/${search}`} replace />
}

// Public Only Route - 로그인 상태에서 접근 시 메인으로 리다이렉트
function PublicOnlyRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, hasCheckedAuth } = useAuthStore()

  if (!hasCheckedAuth) return null

  if (isAuthenticated) {
    return <DefaultRedirect />
  }

  return <>{children}</>
}

function App() {
  const { initializeTenant, tenantId, tenantInfo, isLoading: isTenantLoading, isLanding, error: tenantError } = useTenantStore()
  const { checkAuth, isHydrated, setHydrated, user } = useAuthStore()
  const hasInitialized = useRef(false)

  // 1. Rehydration 설정
  useEffect(() => {
    setHydrated()
  }, [setHydrated])

  // 2. 테넌트 정보 및 인증 초기화
  useEffect(() => {
    if (!isHydrated || hasInitialized.current) return
    hasInitialized.current = true

    const initialize = async () => {
      await initializeTenant()
      await checkAuth()
    }
    initialize()
  }, [isHydrated, initializeTenant, checkAuth])

  // 3. 로딩 상태 처리
  if (!isHydrated || (isTenantLoading && !tenantInfo)) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="inline-block w-8 h-8 rounded-full border-b-2 animate-spin border-primary-600"></div>
          <p className="mt-4 text-gray-600">시스템 초기화 중...</p>
        </div>
      </div>
    )
  }

  const isInvalidTenant = !isTenantLoading && tenantId && !tenantInfo && tenantError

  // 4-a. 랜딩 페이지: 서브도메인 없음
  if (isLanding) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="*" element={<RedirectToHome />} />
        </Routes>
      </BrowserRouter>
    )
  }

  // 4-b. 브랜드 페이지: 유효하지 않은 테넌트
  if (isInvalidTenant) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<BrandPage />} />
          <Route path="*" element={<RedirectToHome />} />
        </Routes>
      </BrowserRouter>
    )
  }

  return (
    <DevToolsProvider userEmail={user?.email}>
    <BrowserRouter>
      <Toaster position="top-right" richColors />
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<PublicOnlyRoute><LoginPage /></PublicOnlyRoute>} />
        {/* /register 라우트 제거 — B2B는 자체 가입 불가 */}
        <Route path="/find-account" element={<FindAccountPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/auth/setup" element={<AccountSetupPage />} />

        {/* Admin Routes (Company Admin) */}
        <Route path="/admin" element={
          <ProtectedRoute adminOnly>
            <AdminLayout />
          </ProtectedRoute>
        }>
          <Route index element={<AdminDashboard />} />
          <Route path="departments" element={<DepartmentManagement />} />
          <Route path="special-groups" element={<SpecialGroupManagement />} />
          <Route path="classes" element={<ClassManagement />} />
          <Route path="members" element={<MemberManagement />} />
          <Route path="products" element={<ProductManagement />} />
          <Route path="products/:subscriptionId" element={<ProductDetail />} />
          <Route path="reports" element={<ProgressReport />} />
          {/* <Route path="aiq-report" element={<AiqClassReport />} /> */}
          <Route path="aiq-learner-report" element={<AiqLearnerReport />} />
          <Route path="notes" element={<NotesPage />} />
          <Route path="messages" element={<AdminMessagesPage />} />
          <Route path="messages/compose" element={<AdminMessageComposePage />} />
          <Route path="messages/:id" element={<AdminMessageDetailPage />} />
          <Route path="contract" element={<ContractManagement />} />
          <Route path="support" element={<AdminSupport />} />
          <Route path="profile" element={<MyProfile />} />
        </Route>

        {/* Learner Routes */}
        <Route path="/my" element={
          <ProtectedRoute learnerOnly>
            <MainLayout />
          </ProtectedRoute>
        }>
          <Route path="dashboard" element={<MyDashboard />} />
          <Route path="courses" element={<MyCourses />} />
          <Route path="history" element={<LearningHistory />} />
          <Route path="aiq" element={<MyAIQ />} />
          <Route path="support" element={<MySupport />} />
          <Route path="notes" element={<NotesPage />} />
          <Route path="profile" element={<MyProfile />} />
        </Route>

        {/* Messages Routes (for learners) */}
        <Route path="/messages" element={
          <ProtectedRoute learnerOnly>
            <MainLayout />
          </ProtectedRoute>
        }>
          <Route index element={<LearnerMessagesPage />} />
          <Route path=":id" element={<LearnerMessageDetailPage />} />
        </Route>

        {/* Learning Page */}
        <Route path="/learn/:classProductId/:contentId?" element={
          <ProtectedRoute learnerOnly>
            <NewLearningPage />
          </ProtectedRoute>
        } />

        {isDevToolsVisible(user?.email) && (
          <>
          <Route path="/test/quiz" element={
            <ProtectedRoute>
              <QuizTestPage />
            </ProtectedRoute>
          } />
          <Route path="/test/tool" element={
            <ProtectedRoute>
              <ToolTestPage />
            </ProtectedRoute>
          } />
          <Route path="/test/queture" element={
            <ProtectedRoute>
              <QuetureTestPage />
            </ProtectedRoute>
          } />
          <Route path="/test/game" element={
            <ProtectedRoute>
              <GameTestPage />
            </ProtectedRoute>
          } />
          <Route path="/review-contents" element={
            <ProtectedRoute learnerOnly>
              <QuizReviewPage />
            </ProtectedRoute>
          } />
          <Route path="/print-sample" element={<PrintSamplePage />} />
          </>
        )}

        {/* AIQ Routes */}
        <Route path="/learn/:classProductId/aiq/pre" element={
          <ProtectedRoute learnerOnly>
            <PreAiqTest />
          </ProtectedRoute>
        } />

        <Route path="/learn/:classProductId/aiq/post" element={
          <ProtectedRoute learnerOnly>
            <PostAiqTest />
          </ProtectedRoute>
        } />

        <Route path="/learn/:classProductId/aiq/result/:type" element={
          <ProtectedRoute learnerOnly>
            <AiqResult />
          </ProtectedRoute>
        } />

        <Route path="/learn/:classProductId/aiq/report" element={
          <ProtectedRoute learnerOnly>
            <AiqReport />
          </ProtectedRoute>
        } />

        {/* Default Redirect - 역할에 따라 적절한 페이지로 리다이렉트 */}
        <Route path="/" element={<DefaultRedirect />} />
        <Route path="*" element={<DefaultRedirect />} />
      </Routes>
    </BrowserRouter>
    </DevToolsProvider>
  )
}

export default App
